import './Summary.css';

import React from 'react';

import { GetQuoteOk } from '../../../../generated/rating-quoting-graphql';
import { countDecimals } from '../../../../utils/countDecimals';
import {
  formatCADCentsOptional,
  formatCADCentsOptionalWithTwoDecimals
} from '../../../../utils/currencyFormat';
import SummaryCoverage from './SummaryCoverage';

interface SummaryProps {
  summaryInput: GetQuoteOk;
  isEndorsement: boolean;
}

// Keep the comment out code for future use
const Summary: React.FC<SummaryProps> = (props) => {
  const { summaryInput, isEndorsement } = props;

  const { quoteNumber, coveragesData, carrierPartner } = summaryInput;

  const zeroPremiumText = isEndorsement ? 'No Change' : 'No Coverage';

  return (
    <div className="summary-container lg:max-w-xs text-left border-b-2 border-gray-400 md:border-0">
      <p className="text-xl md:text-2xl ">
        <b>Summary:</b>
      </p>
      <dl className="summary-details overflow-hidden md:overflow-auto max-h-full border-b-2 border-gray-400 border-dashed md:border-solid">
        <div>
          <p className="mt-2 mb-1 text-base md:text-lg">
            Quote Number - {quoteNumber}
          </p>
          {coveragesData
            .filter(
              (coverageData) => coverageData.name !== 'workersCompensation'
            )
            .map(({ name, chooseCoverage, finalPremium, limits }, index) => (
              <SummaryCoverage
                key={index}
                name={name}
                chooseCoverage={chooseCoverage}
                finalPremium={finalPremium}
                limits={limits}
                zeroPremiumText={
                  carrierPartner === 'StateNational' && name === 'tria'
                    ? 'Included'
                    : zeroPremiumText
                }
                carrierPartner={carrierPartner}
              />
            ))}
        </div>
      </dl>
    </div>
  );
};

interface SummaryContainerProps {
  quoteGroups: Array<{
    lobType: string;
    applicationId: string;
    quoteData: GetQuoteOk;
  }>;
  totalPayable: number;
  isEndorsement: boolean;
}

export const SummaryContainer: React.FC<SummaryContainerProps> = ({
  quoteGroups,
  totalPayable,
  isEndorsement
}) => {
  const { region } = quoteGroups[0]?.quoteData;

  const taxMsg = (() => {
    return region === 'Canada' || isEndorsement
      ? 'Taxes and Fees are not included'
      : 'Including Taxes and Fees';
  })();

  // assume yearlyPremium and totalPayable have at most 2 decimal places
  const total = (() => {
    const finalTotal =
      region === 'Canada'
        ? quoteGroups[0].quoteData.premium // assume Canada has only one product
        : totalPayable;
    return countDecimals(finalTotal) > 0
      ? formatCADCentsOptionalWithTwoDecimals.format(finalTotal)
      : formatCADCentsOptional.format(finalTotal);
  })();

  return (
    <div className="summary-list">
      {quoteGroups.map((g) => (
        <Summary
          key={g.quoteData.quoteId}
          summaryInput={g.quoteData}
          isEndorsement={isEndorsement}
        />
      ))}
      <p className="mt-4">
        <b className="text-lg md:text-2xl">Total:</b>
        <span className="float-right px-4 text-lg md:text-lg ">
          <b className="font-black text-2xl">{total}</b>
          /year
        </span>
      </p>
      <p className="inline-block w-full text-xs text-right font-semibold mb-2">
        {taxMsg}
      </p>
    </div>
  );
};

export default Summary;
